import type { AttendancesSelector } from '@/routes/_staff/attendances/-utils/i18n';

/**
 * 帳票が取るデータの粒度。テーブルの行の型とカラムをこれが決める。
 * 勤怠の法定帳票は「個人 × 期間」と「事業場 × 期間」の 2 系統しかないが、
 * 出す時間の切り口（実労働 / 時間外 / 有給）が帳票ごとに違うため、
 * 給与・賃金の `ReportGrain` と同じく帳票 ID に 1 対 1 で粒度を立てる。
 */
export type AttendanceReportGrain = 'attendanceBook' | 'workingHoursRecord' | 'annualPaidLeaveLedger' | 'overtimeAgreementReport' | 'monthlyAttendanceSummary' | 'paidLeaveObligationReport';
export type AttendanceReportCategory = 'individual' | 'organization' | 'inHouse';

/**
 * `z.enum` にそのまま渡せるよう readonly タプルで持つ。ここから型を導出することで
 * `-zod-schema.ts` 側の `as` キャストが不要になる。
 */
export const STATUTORY_REPORT_IDS = ['attendanceBook', 'workingHoursRecord', 'annualPaidLeaveLedger', 'overtimeAgreementReport', 'monthlyAttendanceSummary', 'paidLeaveObligationReport'] as const;
export type StatutoryReportId = (typeof STATUTORY_REPORT_IDS)[number];
export interface StatutoryReport {
  id: StatutoryReportId;
  category: AttendanceReportCategory;
  grain: AttendanceReportGrain;
}
const STATUTORY_REPORTS: StatutoryReport[] = [
// 出勤簿は労基法 108 条の法定三帳簿の 1 つ。労働日ごとの労働時間を月単位でまとめる
{
  id: 'attendanceBook',
  category: 'individual',
  grain: 'attendanceBook'
},
// 安衛法 66 条の 8 の 3「労働時間の状況の把握」。産業医面接指導の判定根拠になる
{
  id: 'workingHoursRecord',
  category: 'individual',
  grain: 'workingHoursRecord'
},
// 労基則 24 条の 7。基準日・取得日数・時季を労働者ごとに 3 年間保存する
{
  id: 'annualPaidLeaveLedger',
  category: 'individual',
  grain: 'annualPaidLeaveLedger'
}, {
  id: 'overtimeAgreementReport',
  category: 'organization',
  grain: 'overtimeAgreementReport'
}, {
  id: 'monthlyAttendanceSummary',
  category: 'organization',
  grain: 'monthlyAttendanceSummary'
}, {
  id: 'paidLeaveObligationReport',
  category: 'organization',
  grain: 'paidLeaveObligationReport'
}];
export const DEFAULT_REPORT_ID = 'attendanceBook' satisfies StatutoryReportId;
export function findReport(id: StatutoryReportId): StatutoryReport {
  const report = STATUTORY_REPORTS.find(entry => entry.id === id);
  return report ?? STATUTORY_REPORTS[0];
}

// ---------------------------------------------------------------------------
// In-house reports (テナント専用帳票)
// ---------------------------------------------------------------------------

export const IN_HOUSE_REPORT_IDS = ['departmentOvertimeRanking', 'clockingCorrectionSummary', 'shiftFulfillment'] as const;
export type InHouseReportId = (typeof IN_HOUSE_REPORT_IDS)[number];

// ---------------------------------------------------------------------------
// Analysis items (分析)
// ---------------------------------------------------------------------------

export const ANALYSIS_ITEM_IDS = ['analysisList', 'analysisOvertimeTrend', 'analysisPaidLeaveRate', 'analysisNightHolidayWork'] as const;
export type AnalysisItemId = (typeof ANALYSIS_ITEM_IDS)[number];

// ---------------------------------------------------------------------------
// Combined menu ID — URL search param accepts any of the three groups
// ---------------------------------------------------------------------------

export type ReportMenuId = StatutoryReportId | InHouseReportId | AnalysisItemId;
export function isStatutoryReportId(id: ReportMenuId): id is StatutoryReportId {
  return (STATUTORY_REPORT_IDS as readonly string[]).includes(id);
}
export function isInHouseReportId(id: ReportMenuId): id is InHouseReportId {
  return (IN_HOUSE_REPORT_IDS as readonly string[]).includes(id);
}
export function isAnalysisItemId(id: ReportMenuId): id is AnalysisItemId {
  return (ANALYSIS_ITEM_IDS as readonly string[]).includes(id);
}

// ---------------------------------------------------------------------------
// Sidebar structure
// ---------------------------------------------------------------------------

export interface SidebarSection {
  category: AttendanceReportCategory;
  reportIds: ReportMenuId[];
}
export const SIDEBAR_SECTIONS: SidebarSection[] = [{
  category: 'individual',
  reportIds: ['attendanceBook', 'workingHoursRecord', 'annualPaidLeaveLedger']
}, {
  category: 'organization',
  reportIds: ['overtimeAgreementReport', 'monthlyAttendanceSummary', 'paidLeaveObligationReport']
}, {
  category: 'inHouse',
  reportIds: ['departmentOvertimeRanking', 'clockingCorrectionSummary', 'shiftFulfillment']
}];
export const ANALYSIS_LIST_IDS: AnalysisItemId[] = ['analysisList'];
export const ANALYSIS_RECENT_IDS: AnalysisItemId[] = ['analysisOvertimeTrend', 'analysisPaidLeaveRate', 'analysisNightHolidayWork'];

// ---------------------------------------------------------------------------
// i18n selectors
// ---------------------------------------------------------------------------

const statutoryReportSelectors = {
  annualPaidLeaveLedger: $ => $.reports.names.annualPaidLeaveLedger,
  attendanceBook: $ => $.reports.names.attendanceBook,
  monthlyAttendanceSummary: $ => $.reports.names.monthlyAttendanceSummary,
  overtimeAgreementReport: $ => $.reports.names.overtimeAgreementReport,
  paidLeaveObligationReport: $ => $.reports.names.paidLeaveObligationReport,
  workingHoursRecord: $ => $.reports.names.workingHoursRecord
} satisfies Record<StatutoryReportId, AttendancesSelector>;
const inHouseReportSelectors = {
  clockingCorrectionSummary: $ => $.reports.names.clockingCorrectionSummary,
  departmentOvertimeRanking: $ => $.reports.names.departmentOvertimeRanking,
  shiftFulfillment: $ => $.reports.names.shiftFulfillment
} satisfies Record<InHouseReportId, AttendancesSelector>;
export const analysisItemSelectors = {
  analysisList: $ => $.analysis.list,
  analysisNightHolidayWork: $ => $.analysis.recent.nightHolidayWork,
  analysisOvertimeTrend: $ => $.analysis.recent.overtimeTrend,
  analysisPaidLeaveRate: $ => $.analysis.recent.paidLeaveRate
} satisfies Record<AnalysisItemId, AttendancesSelector>;
export const reportMenuItemSelectors: Record<ReportMenuId, AttendancesSelector> = {
  ...statutoryReportSelectors,
  ...inHouseReportSelectors,
  ...analysisItemSelectors
};
export const reportCategorySelectors = {
  inHouse: $ => $.reports.categories.inHouse,
  individual: $ => $.reports.categories.individual,
  organization: $ => $.reports.categories.organization
} satisfies Record<AttendanceReportCategory, AttendancesSelector>;